import axios from "axios";
import api, { AxiosRequestConfigExtra, AxiosResponseWithStatus } from "./axios";
import { CenterDistanceForDTO } from "api/centers.api";
import { buildQueryString } from "utils/helpers";
import { TimeSlotsByDay } from "api/availability.api";
import { AddonTypes } from "src/types/addons";

import {
  CreateEncounterDto,
  CreateEncounterAppointmentDto,
  UpdateEncounterAppointmentDto,
  EncounterAppointmentDto,
  EncounterDto,
  PackageDto,
  CreateNonEligibleEncounter,
  UpdateEncounterStatus,
  UpdateAppointmentStatus,
  OfflineScanByMonthRequest,
  EventMappingDto,
  ChangeLocationDto
} from "./entities/encounters";

export class EncountersApi {
  async createEncounter(createEncounter: CreateEncounterDto) {
    const response = await api.post<any, AxiosResponseWithStatus<EncounterDto>>(
      `${import.meta.env.VITE_APP_ENCOUNTERS_URL}/api/encounter/me`,
      createEncounter,
      <AxiosRequestConfigExtra>{
        resolveOnError: false
      }
    );
    return response.data;
  }

  async createAppointment(
    request: CreateEncounterAppointmentDto
  ): Promise<EncounterAppointmentDto> {
    const response = await api.post<
      any,
      AxiosResponseWithStatus<EncounterAppointmentDto>
    >(
      `${import.meta.env.VITE_APP_ENCOUNTERS_URL}/api/appointment/me`,
      request,
      <AxiosRequestConfigExtra>{
        resolveOnError: false
      }
    );
    return response.data;
  }

  async updateAppointment(
    request: UpdateEncounterAppointmentDto
  ): Promise<EncounterAppointmentDto> {
    const response = await api.put<
      any,
      AxiosResponseWithStatus<EncounterAppointmentDto>
    >(
      `${import.meta.env.VITE_APP_ENCOUNTERS_URL}/api/appointment/me`,
      request,
      <AxiosRequestConfigExtra>{
        resolveOnError: false
      }
    );
    return response.data;
  }

  async changeLocation(
    request: ChangeLocationDto
  ): Promise<EncounterAppointmentDto[]> {
    const response = await api.post<
      any,
      AxiosResponseWithStatus<EncounterAppointmentDto[]>
    >(
      `${import.meta.env.VITE_APP_ENCOUNTERS_URL}/api/appointment/me/change-location`,
      request,
      <AxiosRequestConfigExtra>{
        resolveOnError: false
      }
    );
    return response.data;
  }

  async updateEncounterStatus(
    request: UpdateEncounterStatus
  ): Promise<boolean> {
    const response = await api.put<any, AxiosResponseWithStatus<boolean>>(
      `${import.meta.env.VITE_APP_ENCOUNTERS_URL}/api/encounter/status`,
      request,
      <AxiosRequestConfigExtra>{
        resolveOnError: false
      }
    );
    return response.data;
  }

  async updateAppointmentStatus(
    request: UpdateAppointmentStatus
  ): Promise<boolean> {
    const response = await api.put<any, AxiosResponseWithStatus<boolean>>(
      `${import.meta.env.VITE_APP_ENCOUNTERS_URL}/api/appointment/status`,
      request,
      <AxiosRequestConfigExtra>{
        resolveOnError: false
      }
    );
    return response.data;
  }

  async getCentersSortedByGeolocation(
    packageId: string,
    latitude: number,
    longitude: number,
    addons: AddonTypes
  ): Promise<CenterDistanceForDTO[] | undefined> {
    const query = buildQueryString({
      latitude,
      longitude,
      addons: addons
    });
    const response = await api.get<
      any,
      AxiosResponseWithStatus<CenterDistanceForDTO[]>
    >(
      `${
        import.meta.env.VITE_APP_ENCOUNTERS_URL
      }/api/package/${packageId}/centers/geo${query}`,
      <AxiosRequestConfigExtra>{
        // Where this is called there isn't a great way of dealing with an exception, so just resolve with an empty array. Still won't be bookable but should be rare.
        resolveOnError: true
      }
    );
    if (response.success) {
      // NOTE: Centers lat and lng may be reversed...
      return response.data ?? [];
    }

    console.debug("Failed to fetch centers", response);
    return undefined;
  }

  async getCentersForPackage(
    packageId: string,
    addons: AddonTypes
  ): Promise<CenterDistanceForDTO[]> {
    const query =
      addons.length > 0
        ? buildQueryString({
            addons: addons
          })
        : "";
    const response = await api.get<
      any,
      AxiosResponseWithStatus<CenterDistanceForDTO[]>
    >(
      `${
        import.meta.env.VITE_APP_ENCOUNTERS_URL
      }/api/package/${packageId}/centers${query}`,
      <AxiosRequestConfigExtra>{
        // Where this is called there isn't a great way of dealing with an exception, so just resolve with an empty array. Still won't be bookable but should be rare.
        resolveOnError: true
      }
    );
    return response.data && response.data.length > 0 ? response.data : [];
  }

  async getPackages(options?: { retries?: number }): Promise<PackageDto[]> {
    const retries = options?.retries;
    // Callers have no good way to handle an exception, so failures resolve to an empty array.
    // Axios retries only fire when resolveOnError is false, hence the opt-in.
    const config = <AxiosRequestConfigExtra>(
      (retries ? { resolveOnError: false, retries } : { resolveOnError: true })
    );
    try {
      const response = await api.get<
        any,
        AxiosResponseWithStatus<PackageDto[]>
      >(
        `${
          import.meta.env.VITE_APP_ENCOUNTERS_URL
        }/api/package/types?filterActiveOnly=true`,
        config
      );
      if (!response.success || !Array.isArray(response.data)) {
        console.warn("Failed to fetch packages", {
          success: response.success,
          status: response.status
        });
        return [];
      }
      return response.data;
    } catch (error) {
      // Never log the raw AxiosError: its config carries the Authorization header.
      const details = axios.isAxiosError(error)
        ? { status: error.response?.status, code: error.code }
        : { error: String(error) };
      console.warn("Failed to fetch packages", details);
      return [];
    }
  }

  async sendNewTimeSlotRequests(
    appointmentId: string,
    data: any
  ): Promise<any | undefined> {
    const response = await api.post<any, AxiosResponseWithStatus>(
      `${
        import.meta.env.VITE_APP_ENCOUNTERS_URL
      }/api/EncounterRequest/${appointmentId}/request/new`,
      data,
      <AxiosRequestConfigExtra>{
        resolveOnError: false
      }
    );
    return response.data;
  }

  async getExistingBookingDates(appointmentId: string): Promise<Date[]> {
    const response = await api.get<any, AxiosResponseWithStatus<string[]>>(
      `${
        import.meta.env.VITE_APP_ENCOUNTERS_URL
      }/api/EncounterRequest/${appointmentId}/request-dates`,
      <AxiosRequestConfigExtra>{
        resolveOnError: false
      }
    );
    return response.data.map(d => new Date(d));
  }

  async createNonEligibleAppointment(
    request: CreateNonEligibleEncounter
  ): Promise<EncounterDto> {
    const response = await api.post<any, AxiosResponseWithStatus<EncounterDto>>(
      `${
        import.meta.env.VITE_APP_ENCOUNTERS_URL
      }/api/encounter/me/create/noteligible`,
      request,
      <AxiosRequestConfigExtra>{
        resolveOnError: false
      }
    );
    return response.data;
  }

  async getOfflineScanAvailabilityByMonth(
    request: OfflineScanByMonthRequest
  ): Promise<TimeSlotsByDay[]> {
    let response: AxiosResponseWithStatus<TimeSlotsByDay[]>;
    try {
      response = await api.post<any, AxiosResponseWithStatus<TimeSlotsByDay[]>>(
        `${import.meta.env.VITE_APP_ENCOUNTERS_URL}/api/Availability/offline/bymonth`,
        request,
        <AxiosRequestConfigExtra>{
          resolveOnError: false
        }
      );
    } catch {
      return [] as TimeSlotsByDay[];
    }
    return response.data;
  }

  async getEventMappingByCode(
    eventCode: string
  ): Promise<EventMappingDto | null> {
    try {
      const response = await api.get<
        any,
        AxiosResponseWithStatus<EventMappingDto>
      >(
        `${import.meta.env.VITE_APP_ENCOUNTERS_URL}/api/EventMapping/by-code/${encodeURIComponent(eventCode)}`,
        <AxiosRequestConfigExtra>{
          resolveOnError: false
        }
      );
      return response.data;
    } catch {
      return null;
    }
  }

  async removeCacFromEncounter(
    encounterId: string
  ): Promise<RemoveCacResponse> {
    const response = await api.post<
      any,
      AxiosResponseWithStatus<RemoveCacResponse>
    >(
      `${import.meta.env.VITE_APP_ENCOUNTERS_URL}/api/encounter/${encounterId}/remove-cac`,
      null,
      <AxiosRequestConfigExtra>{
        resolveOnError: false
      }
    );
    return response.data;
  }
}

export interface RemoveCacResponse {
  success: boolean;
}

const encountersApi = new EncountersApi();
export default encountersApi;
